import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import FormDialog from '../components/AccountantCard';
import { Paper, Grid, Box, Button, Tabs, Tab, Avatar, Chip, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from '@mui/styles';
import { urlTariffs, urlAccountants, urlSupervisors, urlAccountingApps, urlFiles } from '../config';
import { useAuthStore } from '../store/authStore';
import { logActionType } from "../services/MyCollections";
import { logActivity } from '../services/ApiServices';
import CustomTooltip from '../components/CustomTooltip';
import * as CommonFunctions from '../services/CommonFunctions';
import * as MyCollections from "../services/MyCollections";
import { useThemeContext } from '../components/contexts/ThemeContext';
import Controls from '../components/controls/Controls';

const useStyles = makeStyles(theme => ({
  pageContent: {
    marginTop: 20
  },
}))

const initialValue = {
  id: null,
  name: '',
  surname: '',
  companyName: '',
  isContract: '',
  nip: '',
  gmailPassword: '',
  isActive: true,
  maxNumberOfClients: '',
  insuranceUpTo: '',
  declaredMaxNumberOfClients: '',
  isMoreClientsAccepted: '',
  isMoreClientsWanted: '',
  clientNumberComment: '',
  forum: '',
  emails: [{
    id: null,
    emailAddress: '',
    contactId: null,
    temailTypeId: null
  }],
  phones: [{
    id: null,
    number: '',
    contactId: null,
    tphoneTypeId: null
  }],
  cooperationHistoryDTOs: [
    {
      id: null,
      cooperationStartDate: '',
      cooperationEndDate: '',
      suspensionStartDate: '',
      suspensionEndDate: '',
      suspended: false,
      contactId: '',
      reasonForTerminatingCooperationId: ''
    }
  ],
  accountantAppDTO: {
    id: null,
    accountingAppId: '',
    accountingAppName: '',
    accountantId: null,
    login: '',
    password: '',
  },
  accountantSupervisorDTOs: [{
    id: null,
    contactIdA: '',
    contactIdB: '',
    name: '',
    surname: '',
    startDate: '',
    endDate: '',
    relationshipTypeId: null
  }],
  accountantTariffDTOs: [{
    id: null,
    accountantId: null,
    startDate: '',
    endDate: '',
    ttariffId: '',
  }],
  fileMetadataDTOS: [{

  }]
}


const ActionRenderer = (props) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Tooltip title="Otwórz kartę księgowej/ego">
        <IconButton
          size="small"
          onClick={() => props.context.handleUpdateAccountant(props.data)}
          sx={{
            backgroundColor: props.context.darkTheme ? 'rgba(148, 163, 184, 0.1)' : 'rgba(71, 85, 105, 0.1)',
            color: props.context.darkTheme ? '#cbd5e1' : '#475569', // Strong Slate Gray
            borderRadius: '10px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: props.context.darkTheme ? 'rgba(148, 163, 184, 0.25)' : 'rgba(71, 85, 105, 0.2)',
              transform: 'scale(1.1)',
            }
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const NameAvatarRenderer = (props) => {
  const { name, surname, insuranceUpTo } = props.data;
  const fullName = `${surname || ''} ${name || ''}`.trim();
  const initials = `${surname?.charAt(0) || ''}${name?.charAt(0) || ''}`;

  const insuranceDate = new Date(insuranceUpTo);
  const currentDate = new Date();
  const oneMonthAhead = new Date(currentDate);
  oneMonthAhead.setMonth(currentDate.getMonth() + 1);
  const isInsuranceExpiring = !props.hideInsuranceWarning && (insuranceDate < oneMonthAhead || !insuranceUpTo);

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '10px', padding: '4px 0' }}>
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: '11px',
          bgcolor: props.context.darkTheme ? '#10b981' : '#059669',
          color: '#ffffff',
          fontWeight: 'bold'
        }}
      >
        {initials}
      </Avatar>
      <span style={{ fontWeight: '600' }}>{fullName}</span>
      {isInsuranceExpiring && (
        <WarningAmberIcon sx={{ color: '#ef4444', fontSize: '18px', flexShrink: 0 }} />
      )}
    </div>
  );
};

const ActiveClientsRenderer = (props) => {
  const { isMoreClientsAccepted, isMoreClientsWanted, activeClients, clientNumberComment } = props.data;
  let color = '#64748b'; // default slate
  let bg = props.context.darkTheme ? '#1e293b' : '#f1f5f9';

  if (isMoreClientsAccepted === false) {
    color = '#ef4444'; // red
    bg = props.context.darkTheme ? '#451a1a' : '#fee2e2';
  } else if (isMoreClientsAccepted) {
    if (isMoreClientsWanted) {
      color = '#10b981'; // green
      bg = props.context.darkTheme ? '#133526' : '#d1fae5';
    } else {
      color = '#f59e0b'; // orange
      bg = props.context.darkTheme ? '#452c08' : '#fef3c7';
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', gap: '6px' }}>
      <Chip
        label={activeClients || 0}
        size="small"
        sx={{
          bgcolor: bg,
          color: color,
          fontWeight: 'normal',
          borderRadius: '6px',
          height: '24px',
          minWidth: '40px'
        }}
      />
      {clientNumberComment && clientNumberComment.trim() !== '' && (
        <ChatBubbleOutlineIcon sx={{ fontSize: '14px', color: props.context.darkTheme ? '#94a3b8' : '#64748b' }} />
      )}
    </div>
  );
};

const AppChipRenderer = (props) => {
  const app = props.data.accountantAppDTO;
  let label = "brak";
  let color = props.context.darkTheme ? '#cbd5e1' : '#475569';
  let bg = props.context.darkTheme ? '#1e293b' : '#f1f5f9';

  if (app) {
    label = app.accountingAppName;
    switch (app.accountingAppId) {
      case 1:
        color = props.context.darkTheme ? '#fda4af' : '#9f1239';
        bg = props.context.darkTheme ? '#4c0519' : '#ffe4e6';
        break;
      case 11:
        color = props.context.darkTheme ? '#6ee7b7' : '#065f46';
        bg = props.context.darkTheme ? '#022c22' : '#d1fae5';
        break;
      case 21:
        color = props.context.darkTheme ? '#93c5fd' : '#1e40af';
        bg = props.context.darkTheme ? '#172554' : '#dbeafe';
        break;
      case 31:
        color = props.context.darkTheme ? '#fcd34d' : '#92400e';
        bg = props.context.darkTheme ? '#451a03' : '#fef3c7';
        break;
      case 41:
        color = props.context.darkTheme ? '#c4b5fd' : '#5b21b6';
        bg = props.context.darkTheme ? '#2e1065' : '#ede9fe';
        break;
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
      <Chip
        label={label}
        size="small"
        sx={{
          bgcolor: bg,
          color: color,
          fontWeight: 'normal',
          borderRadius: '6px',
          height: '24px'
        }}
      />
    </div>
  );
};

const StatusIconRenderer = (props) => {
  const value = props.value;
  const isTrue = value === true || value === 1 || value === 'tak';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      {isTrue ? (
        <CheckCircleIcon sx={{ color: '#10b981', fontSize: '20px' }} />
      ) : (
        <CancelIcon sx={{ color: '#94a3b8', fontSize: '20px' }} />
      )}
    </div>
  );
};


const SupervisorAvatarRenderer = (props) => {
  const fullName = props.value;
  if (!fullName || fullName.trim() === '') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
        <span style={{ color: props.context.darkTheme ? '#94a3b8' : '#64748b' }}>brak</span>
      </div>
    );
  }
  const parts = fullName.split(' ');
  const surname = parts[0] || '';
  const name = parts.length > 1 ? parts[1] : '';
  const initials = `${surname.charAt(0)}${name.charAt(0)}`.toUpperCase();

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '8px', padding: '4px 0' }}>
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: '11px',
          bgcolor: props.context.darkTheme ? '#334155' : '#e2e8f0',
          color: props.context.darkTheme ? '#94a3b8' : '#64748b',
          fontWeight: 'normal'
        }}
      >
        {initials}
      </Avatar>
      <span>{fullName}</span>
    </div>
  );
};

const CopyableTextRenderer = (props) => {
  const text = props.value;
  if (!text || text === 'brak') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
        <span style={{ color: props.context.darkTheme ? '#94a3b8' : '#64748b' }}>brak</span>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
  };

  let iconColor = props.context.darkTheme ? '#94a3b8' : '#64748b';
  if (props.colorType === 'blue') {
    iconColor = props.context.darkTheme ? '#60a5fa' : '#3b82f6';
  } else if (props.colorType === 'red') {
    iconColor = props.context.darkTheme ? '#fb7185' : '#e11d48';
  } else if (props.colorType === 'purple') {
    iconColor = props.context.darkTheme ? '#c084fc' : '#8b5cf6';
  }

  let icon = <PhoneOutlinedIcon sx={{ fontSize: '14px', mr: 1, color: iconColor }} />;
  if (props.colorType === 'blue') {
    icon = <MailOutlineIcon sx={{ fontSize: '14px', mr: 1, color: iconColor }} />;
  } else if (props.colorType === 'red') {
    icon = <AlternateEmailIcon sx={{ fontSize: '14px', mr: 1, color: iconColor }} />;
  } else if (props.colorType === 'purple') {
    icon = <span style={{ marginRight: '6px', fontSize: '10px', fontWeight: 'bold', color: iconColor }}>NIP</span>;
  }

  const textColor = props.context.darkTheme ? '#e2e8f0' : '#334155';

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'space-between', paddingRight: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {icon}
        <span style={{ color: textColor }}>{text}</span>
      </div>
      <Tooltip title="Kopiuj">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ padding: '2px', color: props.context.darkTheme ? '#64748b' : '#94a3b8', '&:hover': { color: textColor } }}
        >
          <ContentCopyIcon sx={{ fontSize: '14px' }} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

function AccountantsList() {
  const { roles: roles } = useAuthStore();

  const token = useAuthStore().token;
  const userName = useAuthStore().fullName;
  // const contactId = useAuthStore().contactId;
  const [gridApi, setGridApi] = useState(null)
  const [tableData, setTableData] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState((JSON.parse(JSON.stringify(initialValue))))
  const [supervisors, setSupervisors] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [accountingApps, setAccountingApps] = useState([]);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const classes = useStyles();
  const [value, setValue] = useState('one');

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const activeAccountants = tableData.filter(item => item.isActive === true);
  const inactiveAccountants = tableData.filter(item => item.isActive === false);
  const activeClientsTotal = activeAccountants.reduce((sum, c) => {
    return sum + (c.activeClients || 0);
  }, 0);
  const isViewOnly = roles?.includes('CHIEF_ACCOUNTANT');
  const isExternalIt = roles?.includes('EXTERNAL_IT');
  const { mode, toggleTheme } = useThemeContext();
  const darkTheme = mode === 'dark';

  const gridRef = useRef();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(JSON.parse(JSON.stringify(initialValue)));
    getAccountants();
  };


  // Each Column Definition results in one Column.
  const columnDefs = useMemo(() => [
    {
      headerName: "",
      field: "id",
      cellRenderer: ActionRenderer,
      minWidth: 50,
      maxWidth: 50,
      suppressMenu: true,
      filter: false,
      sortable: false,
      lockPosition: 'left',
      hide: isExternalIt,
    },
    {
      headerName: "Imię i Nazwisko",
      field: "name",
      lockPosition: 'left',
      minWidth: 190,
      cellRenderer: NameAvatarRenderer,
      cellRendererParams: { hideInsuranceWarning: isExternalIt },
      filter: 'agTextColumnFilter',
      tooltipValueGetter: (params) => {
        if (isExternalIt) return null;
        const insuranceDate = new Date(params.data.insuranceUpTo);
        const currentDate = new Date();
        const oneMonthAhead = new Date(currentDate);
        oneMonthAhead.setMonth(currentDate.getMonth() + 1);
        if (insuranceDate < oneMonthAhead || !params.data.insuranceUpTo) {
          return "Ubezpieczenie wygasa lub brak wpisu!";
        }
        return null;
      },
      valueGetter: (params) => {
        if (params.data.name && params.data.surname) {
          return params.data.surname + " " + params.data.name;
        }
        return "";
      }
    },
    {
      headerName: "Aktywni klienci",
      field: "activeClients",
      minWidth: 90,
      lockPosition: 'left',
      tooltipValueGetter: (params) => params.data.clientNumberComment,
      cellRenderer: ActiveClientsRenderer,
      hide: isExternalIt,
    },
    {
      headerName: "Program",
      field: "program",
      minWidth: 90,
      lockPosition: 'left',
      cellRenderer: AppChipRenderer,
      hide: isExternalIt,
      valueGetter: function (params) {
        return params.data.accountantAppDTO?.accountingAppName || "brak";
      },
    },
    {
      headerName: "Superwizor",
      field: "supervizorFullName",
      minWidth: 160,
      lockPosition: 'left',
      cellRenderer: SupervisorAvatarRenderer,
      hide: isExternalIt,
    },
    {
      headerName: "Poczta",
      field: "delegatedAccount",
      minWidth: 160,
      lockPosition: 'left',
      cellRenderer: CopyableTextRenderer,
      cellRendererParams: { colorType: 'blue' }
    },
    {
      headerName: "Alias",
      field: "aliasSevenPM",
      minWidth: 200,
      lockPosition: 'left',
      cellRenderer: CopyableTextRenderer,
      cellRendererParams: { colorType: 'red' }
    },
    {
      headerName: "Telefon",
      field: "phoneNumber",
      minWidth: 160,
      lockPosition: 'left',
      cellRenderer: CopyableTextRenderer,
      cellRendererParams: { colorType: 'default' }
    },
    {
      headerName: "Forum",
      field: "forum",
      minWidth: 80,
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      cellRenderer: StatusIconRenderer,
      hide: isExternalIt,
      valueGetter: function (params) {
        return params.data.forum === true ? 'tak' : 'nie';
      }
    },
    {
      headerName: "Umowa",
      field: "isContract",
      minWidth: 80,
      lockPosition: 'left',
      cellRenderer: StatusIconRenderer,
      hide: isExternalIt,
      valueGetter: function (params) {
        return params.data.isContract === true ? 'tak' : 'nie';
      }
    },
  ], [isExternalIt]);


  const columnDefs2 = useMemo(() => [
    {
      headerName: "",
      field: "id",
      cellRenderer: ActionRenderer,
      minWidth: 50,
      maxWidth: 50,
      suppressMenu: true,
      filter: false,
      sortable: false,
      lockPosition: 'left',
    },
    {
      headerName: "Imię i Nazwisko",
      field: "name",
      lockPosition: 'left',
      minWidth: 220,
      cellRenderer: NameAvatarRenderer,
      cellRendererParams: { hideInsuranceWarning: true },
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        if (params.data.name && params.data.surname) {
          return params.data.surname + " " + params.data.name;
        }
        return "";
      }
    },
    {
      headerName: "Poczta",
      field: "delegatedAccount",
      minWidth: 200,
      lockPosition: 'left',
      cellRenderer: CopyableTextRenderer,
      cellRendererParams: { colorType: 'blue' }
    },
    {
      headerName: "Alias",
      field: "aliasSevenPM",
      minWidth: 200,
      lockPosition: 'left',
      cellRenderer: CopyableTextRenderer,
      cellRendererParams: { colorType: 'red' }
    },
    {
      headerName: "Telefon",
      field: "phoneNumber",
      minWidth: 160,
      lockPosition: 'left',
      cellRenderer: CopyableTextRenderer,
      cellRendererParams: { colorType: 'default' }
    },
    {
      headerName: "Forum",
      field: "forum",
      minWidth: 80,
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      cellRenderer: StatusIconRenderer,
      valueGetter: function (params) {
        return params.data.forum === true ? 'tak' : 'nie';
      }
    },
    {
      headerName: "Umowa",
      field: "isContract",
      minWidth: 80,
      lockPosition: 'left',
      cellRenderer: StatusIconRenderer,
      valueGetter: function (params) {
        return params.data.isContract === true ? 'tak' : 'nie';
      }
    }
  ], []);


  // calling getAccountants function for first time 
  useEffect(() => {
    getAccountants();
    if (!isExternalIt) {
      getSupervisors();
      getTariffs();
      getAccountingApps();
    }
  }, [isExternalIt]);

  //fetching user data from server
  const getAccountants = () => {
    fetch(urlAccountants + '/all', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(resp => resp.json())
      .then(data => {
        const rowData = data.map(item => {
          const supervisor = item?.accountantSupervisorDTOs?.[0];
          const supervisorName = supervisor ? supervisor.name : '';
          const supervisorSurname = supervisor ? supervisor.surname : '';

          const alias = item.aliasSevenPM || (item.emails?.length > 0 ? item.emails.find(a => a.temailTypeId === 1)?.emailAddress : "brak");
          const phone = item.phoneNumber || (item.phones?.length > 0 ? item.phones.find(a => a.tphoneTypeId === 21)?.number : "brak");
          const delegatedAccount = item.delegatedAccount || (item.emails?.length > 0 ? item.emails.find(a => a.temailTypeId === 41)?.emailAddress : "brak");
          const gmail = item.gmail || (item.emails?.length > 0 ? item.emails.find(a => a.temailTypeId === 11)?.emailAddress : "brak");

          return {
            ...item,
            supervizorFullName: supervisorSurname + ' ' + supervisorName,
            aliasSevenPM: alias,
            phoneNumber: phone,
            delegatedAccount: delegatedAccount,
            gmail: gmail
          };
        });
        setTableData(rowData);
      })
      .catch(error => {
        alert('Błąd pobierania danych:');
      });
  };

  const getSupervisors = () => {
    fetch(urlSupervisors + "/allForSelect", { headers: { 'Authorization': `Bearer ${token}` } })
      .then(resp => resp.json())
      .then(data => {
        const rowData = data.map(item => {
          return {
            key: item.id,
            value: item.surname + ' ' + item.name,
          };
        });
        setSupervisors(rowData);
      })
      .catch(error => {
        alert('Błąd pobierania danych');
      });
  };

  const getTariffs = () => {
    fetch(urlTariffs + "/allForSelect", { headers: { 'Authorization': `Bearer ${token}` } })
      .then(resp => resp.json())
      .then(data => {
        const rowData = data.map(item => {
          return {
            key: item.id,
            value: item.description,
          };
        });
        setTariffs(rowData);
      })
      .catch(error => {
        alert('Błąd pobierania danych');
      });
  };

  const getAccountingApps = () => {
    fetch(urlAccountingApps + "/allForSelect", { headers: { 'Authorization': `Bearer ${token}` } })
      .then(resp => resp.json())
      .then(data => {
        const rowData = data.map(item => {
          return {
            key: item.id,
            value: item.description,
          };
        });
        setAccountingApps(rowData);
      })
      .catch(error => {
        alert('Błąd pobierania danych');
      });
  };

  const onChange = (e) => {
    const { value, id } = e.target
    setFormData({ ...formData, [id]: value })
  }

  const onGridReady = (params) => {
    setGridApi(params)
  }

  const onBtnExport = useCallback(() => {
    if (isExternalIt) {
      gridRef.current.api.exportDataAsCsv({
        columnKeys: ["name", "delegatedAccount", "aliasSevenPM", "phoneNumber"],
        fileName: "aktywni_ksiegowi.csv",
      });
      return;
    }
    const columnsToExport = ["forum", "isContract", "activeClients", "program", "name", "supervizorFullName", "delegatedAccount", "aliasSevenPM", "phoneNumber"];

    gridRef.current.api.exportDataAsCsv({
      columnKeys: columnsToExport,
    });
  }, [isExternalIt]);

  // setting update row data to form data and opening pop up window
  const handleUpdateAccountant = (oldData) => {
    setFormData(oldData)
    handleClickOpen()
  }

  const handleAddAccountant = () => {
    handleClickOpen()
    setFormData(JSON.parse(JSON.stringify(initialValue)))
  }


  const handleFormSubmit = (files = [], filesToDelete = [], additionalFiles = []) => {
    formData.editedBy = userName;
    const method = formData.id ? "PUT" : "POST";
    const endpoint = formData.id ? `/${formData.id}` : '/';

    fetch(urlAccountants + endpoint, {
      method: method,
      body: JSON.stringify(formData),
      headers: {
        'content-type': "application/json",
        'Authorization': `Bearer ${token}`
      }
    }).then(response => {
      if (!response.ok) {
        if (response.status === 400) {
          throw new Error("Księgowa/księgowy o podanym imieniu i nazwisku istnieje już w bazie");
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response.json();
    })
      .then(async (data) => {
        const accountantId = formData.id || data?.id || (Array.isArray(data) ? data[0]?.id : null);

        if (accountantId) {
          if (files && files.length > 0) {
            for (const fileData of files) {
              await CommonFunctions.handleFileSubmit(fileData.file, accountantId, fileData.fileType, MyCollections.fileAssignmentId.ACCOUNTANT_CARD, token);
            }
          }

          if (additionalFiles && additionalFiles.length > 0) {
            for (const fileData of additionalFiles) {
              if (fileData.isLocal && fileData.file !== undefined) {
                const queryExtra = fileData.validFrom && fileData.validTo ? `&validFrom=${fileData.validFrom}&validTo=${fileData.validTo}` : '';
                await CommonFunctions.handleFileSubmit(fileData.file, accountantId, fileData.fileType, MyCollections.fileAssignmentId.ACCOUNTANT_CARD, token, queryExtra);
              }
            }
          }

          if ((!files || files.length === 0) && filesToDelete) {
            filesToDelete.forEach((fileData) => {
              CommonFunctions.deleteFile(fileData.id, token);
            });
          }
        }

        handleClose();
        if (method === "PUT") {
          logActivity(logActionType.ACCOUNTANT_CARD_EDIT + formData.name + " " + formData.surname, userName, token);
        }
        else {
          logActivity(logActionType.ADD_NEW_ACCOUNTANT + formData.name + " " + formData.surname, userName, token);
        }
      })
      .catch(error => {
        alert(error.message); // Display the error message to the user
        console.error('Error:', error);
      });
  };


  const defaultColDef = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
    editable: false,
    flex: 1,
    tooltipComponent: CustomTooltip,
  }

  const clearFilters = useCallback(() => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;

    if (api) {
      api.setFilterModel(null);

      if (api.applyColumnState) {
        api.applyColumnState({ defaultState: { sort: null } });
      } else if (columnApi && columnApi.applyColumnState) {
        columnApi.applyColumnState({ defaultState: { sort: null } });
      } else if (api.setSortModel) {
        api.setSortModel(null);
      }
    }
  }, []);

  return (
    <>
      <Paper className={classes.pageContent} elevation={0}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap="12px"
          sx={{ mb: 2, px: 2, pt: 2 }}
        >
          {/* Lewa strona: Statystyki */}
          <div className="stat-badges-row" style={{ margin: 0 }}>
            <div className="stat-badge stat-badge--blue">
              <i className="fas fa-user-tie"></i>
              Suma aktywnych księgowych: <strong>{activeAccountants?.length}</strong>
            </div>
            {!isExternalIt && (
              <div className="stat-badge stat-badge--green">
                <i className="fas fa-users"></i>
                Suma przypisanych klientów: <strong>{activeClientsTotal}</strong>
              </div>
            )}
          </div>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap="12px"
          sx={{ mb: 2, px: 2 }}
        >
          {/* Zakładki */}
          <Tabs
            value={value}
            onChange={handleChange}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: darkTheme ? "#90caf9" : "#1976d2",
                height: "3px",
                borderRadius: "2px",
                transition: "all 0.3s ease-in-out",
              },
            }}
          >
            <Tab
              value="one"
              label="Aktywni księgowi"
              sx={{
                fontSize: "14px",
                textTransform: "none",
                color: darkTheme ? "#fff" : "#555",
                padding: "10px 16px",
                borderRadius: "8px",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  backgroundColor: darkTheme ? "#333" : "#f0f0f0",
                  color: darkTheme ? "#90caf9" : "#1976d2",
                },
                "&.Mui-selected": {
                  color: darkTheme ? "#90caf9" : "#1976d2",
                  backgroundColor: darkTheme ? "#1e1e1e" : "#e3f2fd",
                },
              }}
            />
            {!isExternalIt && (
              <Tab
                value="two"
                label="Zakończona współpraca"
                sx={{
                  fontSize: "14px",
                  textTransform: "none",
                  color: darkTheme ? "#fff" : "#555",
                  padding: "10px 16px",
                  borderRadius: "8px",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    backgroundColor: darkTheme ? "#333" : "#f0f0f0",
                    color: darkTheme ? "#90caf9" : "#1976d2",
                  },
                  "&.Mui-selected": {
                    color: darkTheme ? "#90caf9" : "#1976d2",
                    backgroundColor: darkTheme ? "#1e1e1e" : "#e3f2fd",
                  },
                }}
              />
            )}
          </Tabs>

          {/* Prawa strona: Przyciski akcji */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gap="8px">
            <Controls.PremiumButton
              variant="glass"
              label="Wyczyść"
              onClick={clearFilters}
              icon="fas fa-filter"
            />

            {(roles?.includes('ADMIN') || roles?.includes('ADMIN_SUPERVISOR') || roles?.includes('MANAGER') || isExternalIt) && (
              <Controls.PremiumButton
                variant="glass"
                label="CSV"
                onClick={onBtnExport}
                icon="fas fa-download"
              />
            )}

            {!isViewOnly && !isExternalIt && (
              <Controls.PremiumButton
                variant="blue"
                label="Dodaj księgową"
                onClick={handleClickOpen}
                icon="fas fa-plus"
              />
            )}
          </Box>
        </Box>

        {value === 'one' ? (
          <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }}>
            <Grid item xs={12}>
              <div className="App">
                <div className="tableData">
                  <div
                    className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                    style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                  >
                    <AgGridReact
                      key={darkTheme ? 'grid-dark-active' : 'grid-light-active'}
                      ref={gridRef}
                      rowData={activeAccountants}
                      context={{ handleUpdateAccountant, darkTheme }}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      onGridReady={onGridReady}
                      pagination={true}
                      paginationPageSize={50}
                      suppressBrowserResizeObserver={true}
                      animateRows={true}
                      rowSelection='single'
                      suppressSizeToFit={true}
                      headerHeight={45}
                      enableBrowserTooltips={false}
                      tooltipShowDelay={0}
                      popupParent={document.body}
                      localeText={MyCollections.localeTextPL}
                      overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                      overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                    />
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        ) : (
          <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }}>
            <Grid item xs={12}>
              <div className="App1">
                <div className="tableData">
                  <div
                    className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                    style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                  >
                    <AgGridReact
                      key={darkTheme ? 'grid-dark-inactive' : 'grid-light-inactive'}
                      ref={gridRef}
                      rowData={inactiveAccountants}
                      context={{ handleUpdateAccountant, darkTheme }}
                      columnDefs={columnDefs2}
                      defaultColDef={defaultColDef}
                      onGridReady={onGridReady}
                      pagination={true}
                      paginationPageSize={50}
                      suppressBrowserResizeObserver={true}
                      animateRows={true}
                      rowSelection='single'
                      suppressSizeToFit={true}
                      headerHeight={45}
                      localeText={MyCollections.localeTextPL}
                      popupParent={document.body}
                      overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                      overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                    />
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        )}
        <FormDialog open={open} handleClose={handleClose}
          onChange={onChange} handleFormSubmit={handleFormSubmit} setFormData={setFormData} formData={formData} supervisors={supervisors} tariffs={tariffs} accountingApps={accountingApps} />
      </Paper >
    </>
  );
}

export default AccountantsList;